/**
 * Copyright 2023-2024 Highway9 Networks Inc.
 */
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState, store } from "..";
import { Zone } from "~/types/zone";
import { VMZService } from "~/services/VMZService";
import { defaultPolicy, Policy } from "~/types/policy";
import { authenticationService, policyService } from "~/services";
import { QOS } from "~/types/qos";
import { QCI } from "~/types/qci";
import { QosService } from "~/services/QoS-service";
import { MetricsQuery } from "~/services/APIServices";
import { MetricsClient } from "~/sw/metrics/metricClient";
import { normalizeTime } from "~/helpers/utils";

export type QOS_Profile = QOS & QCI;

export const zoneMetricsWorker = new MetricsClient();

type initState = {
  data: Zone[];
  current: Zone | null;
  isEdit: boolean;
  open: boolean;
  loading: boolean;
  metricsLoading: boolean;
  metrics: Record<string , {id : string, data : [number, number][]}[]>

  policies: {
    data: Policy[];
    current: Policy;
    isEdit: boolean;
    open: boolean;
  }
  
  qosProfiles: {
    data: QOS_Profile[];
    current: QOS_Profile | null;
    open: boolean;
    qciData: QCI[];
  }

};

export const initialState: initState = {
  data: [],
  current: null,
  isEdit: false,
  open: false,
  policies: {
    data: [],
    current: defaultPolicy,
    isEdit: false,
    open: false,
  },
  loading: true,
  metricsLoading: true,
  metrics: {},
  qosProfiles: {
    data: [],
    qciData: [],
    current: null,
    open: false,
  },

};

export const fetchZones = createAsyncThunk("vmzGroup/fetchZones", async () => {
  try {
    const vmz = await VMZService.getVMZData();
    return vmz;
  } catch (error) {
    console.log(error);
    throw error;
  }
});

export const fetchPolicies = createAsyncThunk("vmzGroup/fetchPolicies", async () => {
  try {
    const policies = await policyService.getPolicies()
    return policies;
  } catch (error) {
    console.log(error);
    throw error;
  }
});

export const fetchQOSProfiles = createAsyncThunk("vmzGroup/fetchQOSProfiles", async () => {
  try {
    const qosData = await QosService.getQosData();
    const qciData = await QosService.getQCIData();
    return { qos: qosData, qci: qciData };
  } catch (error) {
    console.log(error);
    throw error;
  }
});

export const fetchZoneMetrics = createAsyncThunk(`zone/fetchZoneMetrics`, async (query: MetricsQuery, thunkApi) => {
  try {
    const state = thunkApi.getState() as RootState;
    const { interval, timeZone } = state.utility.time;

    query.interval = {
      startTime: normalizeTime(query.interval.startTime, query.resolution, timeZone),
      endTime: normalizeTime(query.interval.endTime, query.resolution, timeZone)
    }

    let data : typeof initialState.metrics = {};
    await zoneMetricsWorker.initialize('zones', query, authenticationService.currentUserValue!.token, (id, metrics) => {
      data = metrics as typeof initialState.metrics;
    }, { noPolling: interval === 'custom', interval : 30});
    
    return data;

  } catch (error : any) {
    const errorMsg = error.errors[0].message;
    return thunkApi.rejectWithValue(errorMsg);
  }
});

export const zoneSlice = createSlice({
  name: "zone",
  initialState,
  reducers: {
    setCurrent: (state, action: PayloadAction<Zone | null>) => {
      state.current = action.payload;
    },
    setZoneOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },

    // Policies
    setPoliciesCurrent: (state, action: PayloadAction<Policy>) => {
      state.policies.current = action.payload ?? structuredClone(defaultPolicy);
    },
    setPoliciesOpen: (state, action: PayloadAction<boolean>) => {
      state.policies.open = action.payload;
    },

    setPoliciesEdit: (state, action: PayloadAction<boolean>) => {
      state.policies.isEdit = action.payload;
    },

    resetCurrentPolicy: (state) => {
      state.policies.current = structuredClone(defaultPolicy);
    },

  
    // QOS Profile
    setQOSProfileCurrent: (state, action: PayloadAction<QOS_Profile | null>) => {
      state.qosProfiles.current = action.payload;
    },

    setQOSProfileOpen: (state, action: PayloadAction<boolean>) => {
      state.qosProfiles.open = action.payload;
    },
    setEdit : (state, action : PayloadAction<boolean>) => {
        state.isEdit = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchZones.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchZones.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(fetchZones.rejected, (state, action) => {
        state.data = [];
        state.loading = false;
        console.log(action.error);
      })
      .addCase(fetchZoneMetrics.pending, (state) => {
        state.metricsLoading = true;
      })
      .addCase(fetchZoneMetrics.fulfilled, (state, action) => {
        const data = action.payload;
        Object.entries(data).forEach(([key, value]) => {
          state.metrics[key] = value;
        });
        state.metricsLoading = false;
      })
      .addCase(fetchZoneMetrics.rejected, (state, action) => {
        state.metricsLoading = false;
        console.log(action.error);
      })

      .addCase(fetchPolicies.fulfilled, (state, action) => {
        // Rebuild immutably instead of assigning onto action.payload: reducers must never
        // mutate their payload — identity-preserving update paths (entity-adapter
        // migration, MCS-3969) hand reducers objects that are frozen in state.
        state.policies.data = action.payload.map((policy) => ({
          ...policy,
          flowRules: policy.flowRules.map((flowRule) => {
            if (flowRule.qosProfile) {
              const selectedQOS = state.qosProfiles.data.find((x) => x.id === flowRule.qosProfile);
              if (!selectedQOS) return flowRule;
              return {
                ...flowRule,
                QosProfileType: selectedQOS.isGbr ? "GBR" : "nonGBR",
                qosProfileName: selectedQOS.name,
              };
            }
            return { ...flowRule, QosProfileType: "default", qosProfileName: "Default" };
          }),
        }));
      })

      .addCase(fetchQOSProfiles.fulfilled, (state, action) => {
        const qosProfile = action.payload.qos.map((item) => {
          const _qciData = action.payload.qci.find((qciObj) => item.qci === qciObj.qci);
          return { ...item, ..._qciData } as QOS_Profile;
        });
        state.qosProfiles.data = qosProfile;
        if (action.payload.qci) {
          state.qosProfiles.qciData = action.payload.qci;
        }
      });
  }
});

export const zoneActions = zoneSlice.actions;
export default zoneSlice.reducer;

export const zoneData = (state: RootState) => state.zone.data;
export const zoneState = (state: RootState) => state.zone.current;
export const zoneOpen = (state: RootState) => state.zone.open;
export const zonesCount = (state: RootState) => state.zone.data.length;
export const zoneLoading = (state: RootState) => state.zone.loading;
export const zoneMetricsLoading = (state: RootState) => state.zone.metricsLoading;
export const zoneMetrics = (state: RootState) => state.zone.metrics;
export const zoneMetric = (metric: string) => (state: RootState) => state.zone.metrics[metric];
export const zoneIsEdit = (state: RootState) => state.zone.isEdit;

export const zonePoliciesData = (state: RootState) => state.zone.policies.data;
export const zonePoliciesState = (state: RootState) => state.zone.policies.current;
export const zonePoliciesIsEdit = (state: RootState) => state.zone.policies.isEdit;
export const zonePoliciesOpen = (state: RootState) => state.zone.policies.open;

export const zoneQOSProfileData = (state: RootState) => state.zone.qosProfiles.data;
export const zoneQOSProfileState = (state: RootState) => state.zone.qosProfiles.current;
export const zoneQOSProfileOpen = (state: RootState) => state.zone.qosProfiles.open;
export const zoneQOSProfileQCI = (state: RootState) => state.zone.qosProfiles.qciData;

/**
 * metrics workers
 */
setTimeout(() => {
  zoneMetricsWorker.eventEmitter().on('worker:data', async (id, workerID, metrics) => {
    store.dispatch({ type: 'zone/fetchZoneMetrics/fulfilled', payload: metrics });
  });
  zoneMetricsWorker.eventEmitter().on('worker:error', async (id, workerID, error) => {
    store.dispatch({ type: 'zone/fetchZoneMetrics/rejected', payload: error });
  });
});